import Alert from '@material-ui/lab/Alert'
import React from 'react'

import { getRoleFromStorage } from '../helpers/userData'
import { getTranslate } from '../localization'
import PromotionBuilder from '../screens/PromotionBuilder'
import PromotionEvaluationTrace from '../screens/PromotionEvaluationTrace'
import PromotionSkipReasons from '../screens/PromotionSkipReasons'
import Promotions from '../screens/Promotions'
import PromotionsOverview from '../screens/PromotionsOverview'
import VoucherUpload from '../screens/VoucherUpload'
import { ROLE, RouteWithAccessType } from './types'

const promotionReaders = [
  ROLE.ADMIN,
  ROLE.TENANT,
  ROLE.TENANT_MARKETING,
  ROLE.TENANT_MANAGEMENT,
  ROLE.PROVIDER,
  ROLE.PROVIDER_MARKETING,
  ROLE.PROVIDER_MANAGEMENT
]

const PromotionAccessGuard: React.FC<{ component?: JSX.Element }> = ({
  component
}) =>
  promotionReaders.includes(getRoleFromStorage()) ? (
    component || <Promotions />
  ) : (
    <Alert severity="error">
      {getTranslate('You do not have access to promotions.')}
    </Alert>
  )

const PromotionBuilderAccessGuard: React.FC = () =>
  promotionReaders.includes(getRoleFromStorage()) ? (
    <PromotionBuilder />
  ) : (
    <Alert severity="error">
      {getTranslate('You do not have access to promotions.')}
    </Alert>
  )

const promotionRoleManagement: RouteWithAccessType[] = [
  {
    route: '/promotions/:id/vouchers',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    component: <PromotionAccessGuard component={<VoucherUpload />} />
  },
  {
    route: '/promotions/evaluation-trace',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    // Guarded like every sibling route. It was the ONLY promotion route rendering its
    // component bare, and `rolesListWithReadAccess` above does not compensate: the
    // routing layer calls canAccessRoute (cache/auth.ts), which returns true immediately
    // when `requiredFeatures` is empty — and it is empty for every promotion route. So
    // the role list on this entry was decorative and nothing enforced it.
    //
    // Pre-existing, but this MR is the right place to fix it: the evaluation trace used
    // to be reachable only through a button rendered inside the guarded list screen, and
    // it is now a persistent tab, which makes the unguarded destination bookmarkable and
    // linkable. The MR that raises the exposure carries the guard.
    component: <PromotionAccessGuard component={<PromotionEvaluationTrace />} />
  },
  {
    // Declared ABOVE '/promotions/:id', like evaluation-trace above it: the parameterised
    // route would otherwise match 'overview' as an id and render the builder against a
    // promotion that does not exist.
    //
    // KEPT AS AN ALIAS now that '/promotions' renders the Overview. Bookmarks and any link
    // written while this was the canonical path keep working. The tab strip matches no tab
    // for this path and falls back to Overview, which is the right active tab for it.
    route: '/promotions/overview',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    // Guarded like every sibling. The role list on the entry is NOT what enforces this —
    // canAccessRoute returns true immediately when requiredFeatures is empty, and it is
    // empty for every promotion route — so the guard component is the actual gate.
    component: <PromotionAccessGuard component={<PromotionsOverview />} />
  },
  {
    // Declared ABOVE '/promotions/:id' for the same reason as its siblings: the
    // parameterised route would match 'skip-reasons' as an id and render the builder
    // against a promotion that does not exist.
    route: '/promotions/skip-reasons',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    // Guarded, and the guard is what enforces it — canAccessRoute returns true immediately
    // when requiredFeatures is empty, which it is for every promotion route, so the role
    // list above is documentation rather than a gate.
    component: <PromotionAccessGuard component={<PromotionSkipReasons />} />
  },
  {
    route: '/promotions/create',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    component: <PromotionBuilderAccessGuard />
  },
  {
    route: '/promotions/:id/edit',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    component: <PromotionBuilderAccessGuard />
  },
  {
    // THE SECTION'S LANDING VIEW, and it is the Overview rather than the list.
    //
    // An operator arriving from the drawer is asking "how are my campaigns doing", not "show
    // me every row". The list is one tab away at '/promotions/list'.
    //
    // Existing links to '/promotions' therefore land on the Overview rather than the list.
    // That is the intended change and the reason it is stated here: breadcrumbs elsewhere
    // point at '/promotions' as the section home (PromotionBuilder, PromotionEvaluationTrace),
    // and a section home showing the aggregate is the correct destination for them.
    route: '/promotions',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    component: <PromotionAccessGuard component={<PromotionsOverview />} />
  },
  {
    // The list, moved off the section root. Declared ABOVE '/promotions/:id' like every
    // other named promotion route, or the parameterised route matches 'list' as an id and
    // renders the builder against a promotion that does not exist.
    route: '/promotions/list',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    // Passed EXPLICITLY rather than relying on the guard's `component || <Promotions />`
    // default. Identical at runtime, and it makes the entry say which screen it renders —
    // the implicit default is why a route table test had to identify this screen by
    // inequality with another route instead of just naming it.
    component: <PromotionAccessGuard component={<Promotions />} />
  },
  {
    // The record page IS the edit form — the portal has no separate read-only detail view
    // (docs/admin-panel/portal-ux-conventions.md section 3). Read-only-ness comes from
    // `canWrite` disabling the fields and hiding Save, not from rendering a different
    // component. `/promotions/:id/edit` above is kept so existing links stay valid; both
    // render the builder, which reads the id from useParams either way.
    route: '/promotions/:id',
    exact: true,
    rolesListWithReadAccess: promotionReaders,
    rolesListWithWriteAccess: promotionReaders,
    requiredFeatures: [],
    component: <PromotionBuilderAccessGuard />
  }
]

export default promotionRoleManagement
